import svgPaths from "../imports/svg-6sirlc3is8";
import { motion, type Transition } from "framer-motion";
import type { ReactNode } from "react";
import BurgerArrow from "./BurgerArrow";

interface BroadwayBurgerProps {
  isExploded: boolean;
}

const BEIGE = "#F2F1DE";

const enterTransition: Transition = {
  duration: 0.3,
  ease: [0.4, 0, 0.2, 1],
};

const leaveTransition: Transition = {
  type: "spring",
  stiffness: 550,
  damping: 18,
  mass: 1,
};

function getTransition(isExploded: boolean): Transition {
  return isExploded ? enterTransition : leaveTransition;
}


function LabelText({
  lines,
  className = "",
  lineClassNames = [],
}: {
  lines: string[];
  className?: string;
  lineClassNames?: string[];
}) {
  return (
    <div
      className={[
        "flex flex-col justify-center whitespace-nowrap",
        "font-['Diary_Notes',sans-serif] text-[#f2f1de]",
        "text-[8px] leading-[8px] tracking-[0.16px]",
        "min-[380px]:text-[9px] min-[380px]:leading-[9px] min-[380px]:tracking-[0.18px]",
        "sm:text-[10px] sm:leading-[10px] sm:tracking-[0.2px]",
        "lg:text-[12px] lg:leading-[12px] lg:tracking-[0.24px]",
        className,
      ].join(" ")}
    >
      {lines.map((line, index) => (
        <p
          key={`${line}-${index}`}
          className={["mb-0", lineClassNames[index] ?? ""].join(" ")}
        >
          {line}
        </p>
      ))}
    </div>
  );
}

function FlowLabel({
  isExploded,
  side,
  inset = 0,
  children,
}: {
  isExploded: boolean;
  side: "left" | "right";
  inset?: number;
  children?: ReactNode;
}) {
  const explodedX = side === "left" ? inset : -inset;
  const hiddenX = side === "left" ? explodedX - 8 : explodedX + 8;

  return (
    <motion.div
      initial={false}
      className={[
        "pointer-events-none flex shrink-0 overflow-visible",
        side === "left" ? "justify-end" : "justify-start",
      ].join(" ")}
      animate={{
        opacity: isExploded && children ? 1 : 0,
        x: isExploded && children ? explodedX : hiddenX,
      }}
      transition={getTransition(isExploded)}
      aria-hidden={!isExploded}
    >
      <div className="flex items-center gap-2 whitespace-nowrap">
        {children}
      </div>
    </motion.div>
  );
}

function SideLabel({
  isExploded,
  side,
  lines,
  arrowDirection,
  arrowColor,
}: {
  isExploded: boolean;
  side: "left" | "right";
  lines: string[];
  arrowDirection: "left" | "right" | "left-bottom" | "right-top" | "right-bottom";
  arrowColor?: string;
}) {
  return (
    <FlowLabel isExploded={isExploded} side={side} inset={24}>
      {side === "left" ? (
        <>
          <LabelText lines={lines} />
          <BurgerArrow direction={arrowDirection} color={arrowColor} />
        </>
      ) : (
        <>
          <BurgerArrow direction={arrowDirection} color={arrowColor} />
          <LabelText lines={lines} />
        </>
      )}
    </FlowLabel>
  );
}

function LayerRow({
  isExploded,
  y,
  left,
  right,
  children,
}: {
  isExploded: boolean;
  y: number;
  left?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <motion.div
      className="
        mx-auto grid w-fit items-center justify-center gap-x-1
        grid-cols-[68px_133px_68px]
        sm:grid-cols-[80px_133px_80px]
        md:grid-cols-[94px_133px_94px]
      "
      animate={{ y: isExploded ? y : 0 }}
      transition={getTransition(isExploded)}
    >
      <div className="flex h-0 items-center justify-end overflow-visible">
        {left ?? null}
      </div>

      <div className="flex items-center justify-center">
        {children}
      </div>

      <div className="flex h-0 items-center justify-start overflow-visible">
        {right ?? null}
      </div>
    </motion.div>
  );
}

function TopBun() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[36px] rounded-tl-[80px] rounded-tr-[80px] rounded-bl-[8px] rounded-br-[8px] border-4 border-beige-500 sm:h-[40px] md:h-[43.75px]" />
    </div>
  );
}

function SalsaStacyLayer() {
  return (
    <div className="w-[108px] -scale-y-100 p-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[7px] sm:h-[7.5px] md:h-[7.875px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 133 11.2399"
        >
          <path d={svgPaths.p38f39180} stroke={BEIGE} strokeWidth="4" />
        </svg>
      </div>
    </div>
  );
}

function OnionLayer() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[5.8px] sm:h-[6.2px] md:h-[6.663px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 133.416 10.5757"
        >
          <g>
            <path d="M0 6.95627H89.5334" stroke={BEIGE} strokeWidth="4" />
            <path d="M88.3393 1.95627L133 8.61942" stroke={BEIGE} strokeWidth="4" />
          </g>
        </svg>
      </div>
    </div>
  );
}

function CheeseMeatShape() {
  return (
    <div className="w-[108px] p-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[16px] sm:h-[18px] md:h-[19px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 133 23"
        >
          <g>
            <path d={svgPaths.p36715d00} fill={BEIGE} />
            <path d={svgPaths.p13a95540} stroke={BEIGE} strokeLinecap="round" strokeWidth="4" />
            <path d={svgPaths.pca7c900} stroke={BEIGE} strokeLinecap="round" strokeWidth="4" />
          </g>
        </svg>
      </div>
    </div>
  );
}

function CheeseLayer1() {
  return <CheeseMeatShape />;
}

function MeatCheeseLayer() {
  return <CheeseMeatShape />;
}

function SauceLayer() {
  return (
    <div className="w-[108px] p-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[7px] sm:h-[7.5px] md:h-[7.875px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 133 11.2399"
        >
          <path d={svgPaths.p38f39180} stroke={BEIGE} strokeWidth="4" />
        </svg>
      </div>
    </div>
  );
}

function BottomBun() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[16px] rounded-[8px] border-4 border-beige-500 sm:h-[17px] md:h-[18.75px]" />
    </div>
  );
}

function SubtitleContainer() {
  return (
    <div className="relative flex h-[42px] shrink-0 flex-col items-center justify-center leading-none sm:h-[46px] md:h-[48px]">
      <div className="relative z-10 flex flex-col justify-end whitespace-nowrap text-center font-['Biro_Script_Plus',sans-serif] text-[20px] tracking-[0.48px] text-beige-500 sm:text-[22px] md:text-[24px]">
        <p className="leading-[16px] sm:leading-[18px] md:leading-[20px]">americana</p>
      </div>

      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="relative h-[20px] w-[66px] sm:h-[21px] sm:w-[70px] md:h-[22.847px] md:w-[74.754px]">
          <svg
            className="absolute left-[1.64%] top-0 block h-full w-full"
            fill="none"
            preserveAspectRatio="none"
            viewBox="0 0 74.7529 22.8465"
          >
            <g>
              <path d={svgPaths.p183e9000} fill="var(--beige-500)" />
              <path d={svgPaths.p1b131200} fill="var(--beige-500)" />
              <path d={svgPaths.p5461f00} fill="var(--beige-500)" />
              <path d={svgPaths.p3c6ede80} fill="var(--beige-500)" />
              <path d={svgPaths.pc384870} fill="var(--beige-500)" />
              <g>
                <path d={svgPaths.p107ada80} fill="var(--beige-500)" />
                <path d={svgPaths.p2640e100} fill="var(--beige-500)" />
                <path d={svgPaths.p2bb20200} fill="var(--beige-500)" />
                <path d={svgPaths.p131c9700} fill="var(--beige-500)" />
                <path d={svgPaths.p17ac8970} fill="var(--beige-500)" />
                <path d={svgPaths.p40f67f0} fill="var(--beige-500)" />
                <path d={svgPaths.p25fd4f00} fill="var(--beige-500)" />
                <path d={svgPaths.p3c16ae40} fill="var(--beige-500)" />
                <path d={svgPaths.p87b7600} fill="var(--beige-500)" />
                <path d={svgPaths.p15c94680} fill="var(--beige-500)" />
                <path d={svgPaths.pb78be80} fill="var(--beige-500)" />
                <path d={svgPaths.p2cd03900} fill="var(--beige-500)" />
                <path d={svgPaths.pd8c3480} fill="var(--beige-500)" />
                <path d={svgPaths.p7214900} fill="var(--beige-500)" />
                <path d={svgPaths.p23ff5280} fill="var(--beige-500)" />
                <path d={svgPaths.p29320f00} fill="var(--beige-500)" />
                <path d={svgPaths.p19484500} fill="var(--beige-500)" />
                <path d={svgPaths.p2c0e9280} fill="var(--beige-500)" />
                <path d={svgPaths.pcdfe900} fill="var(--beige-500)" />
              </g>
            </g>
          </svg>

          <svg
            className="absolute left-0 top-[45.66%] block h-[11px] w-[65px] sm:h-[12px] sm:w-[69px] md:h-[12.731px] md:w-[73.64px]"
            fill="none"
            preserveAspectRatio="none"
            viewBox="0 0 73.6402 12.7314"
          >
            <g>
              <path d={svgPaths.p36e28400} fill="var(--beige-500)" />
              <path d={svgPaths.p3dd55970} fill="var(--beige-500)" />
              <path d={svgPaths.p995ce00} fill="var(--beige-500)" />
              <path d={svgPaths.p2c1e1600} fill="var(--beige-500)" />
              <path d={svgPaths.p174b4200} fill="var(--beige-500)" />
              <g>
                <path d={svgPaths.p29ea6740} fill="var(--beige-500)" />
                <path d={svgPaths.p11046780} fill="var(--beige-500)" />
                <path d={svgPaths.p36a3f900} fill="var(--beige-500)" />
                <path d={svgPaths.p8e63700} fill="var(--beige-500)" />
                <path d={svgPaths.p36203230} fill="var(--beige-500)" />
                <path d={svgPaths.p162819e0} fill="var(--beige-500)" />
                <path d={svgPaths.p3817ca70} fill="var(--beige-500)" />
                <path d={svgPaths.p90afd00} fill="var(--beige-500)" />
                <path d={svgPaths.p196081f0} fill="var(--beige-500)" />
                <path d={svgPaths.p1a21e4f0} fill="var(--beige-500)" />
              </g>
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
}

export default function BroadwayBurger({ isExploded }: BroadwayBurgerProps) {
  const transition = getTransition(isExploded);

  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col items-center justify-center px-2 sm:px-3">
      <div className="flex w-full flex-col items-center gap-0">
        <LayerRow
          isExploded={isExploded}
          y={-60}
          right={
            <SideLabel
              isExploded={isExploded}
              side="right"
              lines={["Pan ", "BRIOCHE"]}
              arrowDirection="right"
              arrowColor="var(--beige-500)"
            />
          }
        >
          <TopBun />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={-45}
          left={
            <SideLabel
              isExploded={isExploded}
              side="left"
              lines={["SALSA", "STACY"]}
              arrowDirection="left"
              arrowColor="var(--beige-500)"
            />
          }
        >
          <SalsaStacyLayer />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={-30}
          right={
            <SideLabel
              isExploded={isExploded}
              side="right"
              lines={["CEBOLLA", "STACY"]}
              arrowDirection="right"
              arrowColor="var(--beige-500)"
            />
          }
        >
          <OnionLayer />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={-15}
          left={
            <SideLabel
              isExploded={isExploded}
              side="left"
              lines={["CHEDDAR", "X4"]}
              arrowDirection="left"
              arrowColor="var(--beige-500)"
            />
          }
        >
          <CheeseLayer1 />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={0}
          right={
            <SideLabel
              isExploded={isExploded}
              side="right"
              lines={["CARNE", "X2"]}
              arrowDirection="right-bottom"
              arrowColor="var(--beige-500)"
            />
          }
        >
          <MeatCheeseLayer />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={15}
          left={
            <SideLabel
              isExploded={isExploded}
              side="left"
              lines={["MAYO", "THOUSAND"]}
              arrowDirection="left-bottom"
              arrowColor={BEIGE}
            />
          }
        >
          <div className="-scale-y-100">
            <SauceLayer />
          </div>
        </LayerRow>

        <motion.div
          className="
            mx-auto grid w-fit items-center justify-center gap-x-1
            grid-cols-[68px_133px_68px]
            sm:grid-cols-[80px_133px_80px]
            md:grid-cols-[94px_133px_94px]
          "
          animate={{ y: isExploded ? 30 : 0 }}
          transition={transition}
        >
          <div />
          <div className="flex justify-center">
            <BottomBun />
          </div>
          <div />
        </motion.div>
      </div>

      <motion.div
        className="mt-4 flex w-full flex-wrap items-center justify-center gap-4 sm:gap-5 md:gap-6"
        animate={{ y: isExploded ? 40 : 0 }}
        transition={transition}
      >
        <p className="whitespace-nowrap text-center font-['Roboto_Mono',monospace] text-[28px] font-bold leading-none tracking-[0.06em] text-beige-500 sm:text-[36px] md:text-[44px] lg:text-[48px]">
          BROADWAY
        </p>
        <SubtitleContainer />
      </motion.div>
    </div>
  );
}

export function BroadwayBurgerClosed() {
  return <BroadwayBurger isExploded={false} />;
}

export function BroadwayBurgerExploded() {
  return <BroadwayBurger isExploded={true} />;
}